import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { EventListComponent } from 'app/components/event/event-list/event-list.component';
import { GroupService, ClaimsService, NotifierService, EventService, TemplateService } from 'app/services';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import * as models from '../../../models';
import { HelperService } from 'app/services';
import * as viewmodels from 'app/viewmodels';
import { MatDialog } from '@angular/material/dialog';
import { EventAddComponent, EventAddFromLibraryDialogComponent } from 'app/dialogs';
import { Timestamp } from '@angular/fire/firestore';
import * as moment from 'moment';

const returnUrl: string = '/console/task-groups';

@Component({
    selector: 'app-task-group-detail',
    templateUrl: './task-group-detail.component.html',
    styleUrls: ['./task-group-detail.component.scss'],
    standalone: false
})
export class TaskGroupDetailComponent implements OnInit, OnDestroy {

  @ViewChild(EventListComponent) eventListComponent: EventListComponent;

  company: models.Company;
  pageLoaded: boolean = false;
  collectionType: string = "groups";
  taskGroup: models.Group;
  initialTaskGroup: models.Group;
  taskGroupId: string;
  templateUsedForGroup: models.Template;

  events: Array<models.Event>;
  startDate: Timestamp;
  endDate: Timestamp;
  nextDueDate: Timestamp;
  nextDueDaysDif: number;

  paramSub: Subscription;

  //These read through a @ViewChild that isn't resolved on the first change
  //detection pass, so they normalize to a real boolean — otherwise the binding
  //flips undefined -> false after view init and dev mode reports
  //ExpressionChangedAfterItHasBeenCheckedError.
  get eventsDirty(): boolean {
    return this.eventListComponent?.hasPendingChanges === true;
  }

  get saveBtnEnabled(): boolean {
    return this.hasPendingChanges || this.eventsDirty;
  }

  //Live Firestore Timestamps serialize with an extra `type: "firestore/timestamp/1.0"`
  //marker that deep-copied plain objects don't have, so a raw JSON comparison reports
  //every task group as dirty. Strip the marker so only real data differences count.
  private normalizedJson(obj: any): string {
    return JSON.stringify(obj, (key, value) =>
      key === 'type' && typeof value === 'string' && value.startsWith('firestore/timestamp/') ? undefined : value);
  }

  get hasPendingChanges() {
    return this.normalizedJson(this.taskGroup) != this.normalizedJson(this.initialTaskGroup);
  }

  get canEdit() : boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.editGroup);
  }

  get canCompleteEvent(): boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.completeEvent);
  }

  get canAddEvent(): boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.addEvent);
  }

  // ── KPI strip ────────────────────────────────────────────────────────

  get numberOfEvents() {
    return this.events != null ? this.events.length : 0;
  }

  get numberOfCompletedEvents() {
    if (this.events == null) return 0;
    return this.events.filter(e => e.completed).length;
  }

  get percentOfEventsCompletedString() {
    if (this.numberOfEvents == 0) return '0%';
    return ((this.numberOfCompletedEvents / this.numberOfEvents) * 100) + '%';
  }

  get overdueCount(): number {
    if (this.events == null) return 0;
    let startOfToday = moment().startOf('day').valueOf();
    return this.events.filter(e =>
      !e.completed && e.startDateTime != null && e.startDateTime.seconds * 1000 < startOfToday).length;
  }

  get nextDueDaysDifAbs(): number {
    return Math.abs(this.nextDueDaysDif);
  }

  get assigneeCount(): number {
    let assignees = new Set<string>();
    (this.events ?? []).forEach(e => {
      (e.assignedUserIds ?? []).forEach(id => assignees.add(`u:${id}`));
      (e.assignedTeamIds ?? []).forEach(id => assignees.add(`t:${id}`));
    });
    return assignees.size;
  }

  get unassignedCount(): number {
    return (this.events ?? []).filter(e =>
      (e.assignedUserIds ?? []).length == 0 && (e.assignedTeamIds ?? []).length == 0).length;
  }

  private eventMinutes(val: any): number {
    if (val == null || val === '') return 0;
    let num = Number(val);
    return isNaN(num) ? 0 : num;
  }

  get laborSpentMinutes(): number {
    return (this.events ?? []).reduce((sum, e) => sum + this.eventMinutes(e.timeSpent), 0);
  }

  get laborEstMinutes(): number {
    return (this.events ?? []).reduce((sum, e) => sum + this.eventMinutes(e.estimatedTime), 0);
  }

  get laborEventCount(): number {
    return (this.events ?? []).filter(e =>
      this.eventMinutes(e.timeSpent) > 0 || this.eventMinutes(e.estimatedTime) > 0).length;
  }

  get showLaborTile(): boolean {
    return this.laborSpentMinutes > 0 || this.laborEstMinutes > 0;
  }

  formatMinutes(minutes: number): string {
    if (minutes < 60) return `${minutes}m`;
    let hours = minutes / 60;
    return `${Math.round(hours * 10) / 10}h`;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private claimsService: ClaimsService,
    private groupService: GroupService,
    private helperService: HelperService,
    private router: Router,
    private titleService: Title,
    private notifierService: NotifierService,
    private dialog: MatDialog,
    private eventService: EventService,
    private templateService: TemplateService
  ) {
  }

  ngOnInit(): void {
    this.company = this.helperService.currentCompany;
    this.paramSub = this.activatedRoute.params.subscribe(params => {
      this.taskGroupId = params.id;
      this.getTaskGroup();
    });
  }

  ngOnDestroy() {
    if (this.paramSub != null) {
      this.paramSub.unsubscribe();
    }
  }

  canDeactivate() : boolean {
    return !this.hasPendingChanges && !this.eventsDirty;
  }

  //One-time read: a live snapshot would clobber in-progress edits (renames, staged
  //task completions) every time a cloud function touched the group's task counts.
  async getTaskGroup() {
    let taskGroupResponse = await this.groupService.get(this.taskGroupId).get().toPromise();

    if (!taskGroupResponse.exists) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    //stale from the group we just navigated away from
    this.templateUsedForGroup = null;

    this.taskGroup = taskGroupResponse.data();
    this.taskGroup.uid = taskGroupResponse.id;
    this.initialTaskGroup = this.helperService.deepCopy(this.taskGroup);
    this.titleService.setTitle(`${this.taskGroup.name} - Planacan`);

    if (this.taskGroup.templateId != null) {
      this.templateUsedForGroup = await this.templateService.get(this.taskGroup.templateId);
    }
  }

  eventListChange(events: Array<models.Event>){
    this.events = events;

    if (events == null || events.length == 0) {
      //an emptied group must not keep showing the previous group's dates, and
      //there's nothing worth putting in the KPI strip
      this.startDate = null;
      this.endDate = null;
      this.nextDueDate = null;
      this.nextDueDaysDif = null;
      this.pageLoaded = false;
      return;
    }

    let sorted: models.Event[] = this.helperService.sortArrayByNumberField(this.events, 'startDateTime');
    this.startDate = sorted[0].startDateTime;
    this.endDate = sorted[sorted.length - 1].startDateTime;

    //next outstanding task — the earliest incomplete one, overdue or not
    let nextEvent = sorted.find(e => !e.completed && e.startDateTime != null);
    this.nextDueDate = nextEvent?.startDateTime ?? null;
    this.nextDueDaysDif = this.nextDueDate != null
      ? moment(this.nextDueDate.seconds * 1000).startOf('day').diff(moment().startOf('day'), 'days')
      : null;

    this.pageLoaded = true;
  }

  navigateToTemplateDetails() {
    this.router.navigate(['console', 'template', this.templateUsedForGroup.uid]);
  }

  returnToList() {
    this.router.navigateByUrl(returnUrl)
  }

  async saveChanges() {
    if(this.hasPendingChanges) {
      this.saveGroupChanges();
    }
    if(this.eventsDirty){
      this.eventListComponent.saveChanges();
    }
  }

  undoChanges() {
    if(this.hasPendingChanges) {
      this.getTaskGroup()
    }
    if(this.eventsDirty){
      this.eventListComponent.undoChanges();
    }
  }

  async saveGroupChanges() {
    let name = this.taskGroup.name;
    //update() strips uid off the model before writing, so re-read to restore it
    //and to re-baseline the dirty check
    await this.groupService.update(this.taskGroup)
    this.notifierService.success(`Successfully saved Task Group '${name}'`);
    this.getTaskGroup();
  }

  saveName(event) {
    event.target.blur()
    this.saveChanges()
  }

  backToGroupList() {
    this.router.navigate(['console', 'task-groups']);
  }

  addEvent() {
    let dialogRef = this.dialog.open(EventAddComponent, {
      panelClass: 'med-width-dialog',
      data: {
        groupId: this.taskGroupId,
        addFromGroupDetail: true,
      },
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(async result => {
      this.processEventAddResponse(result);
    });
  }

  addEventFromLibrary() {
    let inObj: any = {
      groupId: this.taskGroupId,
    }
    let dialogRef = this.dialog.open(EventAddFromLibraryDialogComponent, {
      data: inObj
    });

    dialogRef.afterClosed().subscribe((result: viewmodels.EventAddResponse) => {
      this.processEventAddResponse(result);
    })
  }

  async processEventAddResponse(result: viewmodels.EventAddResponse) {
    if(result && result.save){
      //perform save
      let assignToGroup: boolean = result.assignToGroup;
      let harvest: models.Harvest = result.harvest;
      let group: models.Group = result.group;

      let events: Array<models.Event> = result.events;
      let dbEvents: Array<models.Event> = [];

      events.forEach(event => {
        let dbEvent: models.Event = event;

        dbEvent.color = assignToGroup ? group.color : harvest.color;

        if(assignToGroup){
          delete dbEvent.harvestName;
          dbEvent.groupName = group.name;
        } else {
          delete dbEvent.groupName;
          dbEvent.harvestName = harvest.name;
        }

        dbEvents.push(dbEvent);
      });

      await this.eventService.add(assignToGroup, assignToGroup ? group.uid : harvest.uid, dbEvents);
    }
  }
}
